import { createSlice } from "@reduxjs/toolkit";

import { executeListOfficeInfos } from "../api/OfficeApi";
import { storeKeyName } from "../common/AppConstant";
import { handleError } from "../common/ErrorUtil";
import { dbKey, sortRule, sortType } from "../model/Common";

import { fetchChangeFilePrefix } from "./FileStore";

/**
 * execSort.
 * ソート処理実行
 *
 */
const execSort = (data, key, type) => {
  const result = data.sort((a, b) => {
    let resultA = a[key];
    let resultB = b[key];

    // 工番の場合のみ、加工処理を実施
    // ※親番、枝番毎に昇順ソートを判定する
    if (key === dbKey.pressNo) {
      // 工番を親番、枝番に分割
      const splitA = resultA.split("-");
      const splitB = resultB.split("-");

      // 親番：数値的な昇順を行うため、0パディングによる桁揃え状態に変換
      const parentA = splitA[0].padStart(10, "0");
      const parentB = splitB[0].padStart(10, "0");

      // 枝番：存在する場合、0パディングによる桁揃え状態に変換
      const branchInit = "0000000000";
      let branchA = branchInit;
      if (splitA.length > 1) {
        branchA = splitA[1].padStart(10, "0");
      }
      let branchB = branchInit;
      if (splitB.length > 1) {
        branchB = splitB[1].padStart(10, "0");
      }

      // 変換後の値を、再度工番として結合
      resultA = `${parentA}-${branchA}`;
      resultB = `${parentB}-${branchB}`;
    }

    // 大小比較
    if (resultA > resultB) return sortRule[type][0];
    if (resultA < resultB) return sortRule[type][1];
    return 0;
  });

  return result;
};

/**
 * Slice.
 *
 * @type {object}
 */
export const slice = createSlice({
  // Slice名
  name: storeKeyName.office,

  // 初期State
  initialState: {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading: false,
    // エラー内容
    error: null,
    // 拠点情報一覧(工番昇順ソート済み)
    officeInfos: [],
    // 拠点情報一覧(工番管理画面向け / ユーザー指定項目でソート済み)
    sortedOfficeInfos: [],
    // 選択されている拠点情報
    selectedOfficeInfo: {
      serialNumber: "",
      label: "",
      pressType: "",
    },
  },

  // Reducer
  reducers: {
    // 通信開始
    fetchStart(state) {
      state.isLoading = true;
    },
    // 通信失敗
    fetchFailure(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
    // 通信成功
    fetchSuccess(state, action) {
      state.error = null;
      state.officeInfos = action.payload;
      state.sortedOfficeInfos = action.payload;
      if (!state.selectedOfficeInfo) {
        state.selectedOfficeInfo = state.officeInfos.find(
          (info) => info.serialNumber === state.selectedOfficeInfo.serialNumber
        );
      }
      state.isLoading = false;
    },
    // クリア
    fetchClear(state) {
      state.error = null;
      state.officeInfos = [];
      state.sortedOfficeInfos = [];
      state.selectedOfficeInfo = {
        serialNumber: "",
        label: "",
        pressType: "",
      };
      state.isLoading = false;
    },
    // 選択されている拠点情報の変更
    fetchChangeSelectedOfficeInfo(state, action) {
      state.selectedOfficeInfo = state.officeInfos.find(
        (info) => info.serialNumber === action.payload
      );
    },
    // 指定キーによるソート実施
    fetchChangeSortedOfficeInfos(state, action) {
      state.sortedOfficeInfos = execSort(
        state.officeInfos,
        action.payload.key,
        action.payload.type
      );
    },
  },
});

/**
 * Actions.
 *
 * @type {object}
 */
export const {
  fetchStart,
  fetchFailure,
  fetchSuccess,
  fetchClear,
  fetchChangeSelectedOfficeInfo,
  fetchChangeSortedOfficeInfos,
} = slice.actions;

/**
 * Selectors.
 *
 * @type {function}
 */
export const selector = (stateAll) =>
  // すべてのStoreのStateが含まれているため、特定のStoreのStateのみ返す
  stateAll[storeKeyName.office];

/**
 * fetchUpdateSelectedOfficeInfo.
 * 選択済み拠点情報更新
 *
 */
export const fetchUpdateSelectedOfficeInfo = (serialNumber) => async (
  dispatch
) => {
  dispatch(fetchChangeSelectedOfficeInfo(serialNumber));
  dispatch(fetchChangeFilePrefix(`${serialNumber}/`));
};

/**
 * fetchListOfficeInfos.
 * 拠点情報一覧取得
 *
 */
export const fetchListOfficeInfos = () => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { data, error } = await executeListOfficeInfos().catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  // 工番で昇順ソート
  const payload = execSort(data.officeInfos, dbKey.pressNo, sortType.asc);
  dispatch(fetchSuccess(payload));
};

/**
 * fetchSortOfficeInfos.
 * 拠点情報一覧取得
 *
 */
export const fetchUpdateSortedOfficeInfos = (payload) => async (dispatch) => {
  dispatch(fetchChangeSortedOfficeInfos(payload));
};
